import { FC } from 'react';
import { Box, Button, Dialog, IconButton, Paper, Typography, useTheme } from '@mui/material';
import { observer } from 'mobx-react-lite';

import { Trans } from '@libs/i18n';
import { PaperPlaneTiltIcon, WarningIcon, XIcon } from '@phosphor-icons/react';

interface ConfirmSubmissionModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isSubmitting: boolean;
}

export const ConfirmSubmissionModal: FC<ConfirmSubmissionModalProps> = observer(
  ({ open, onClose, onConfirm, isSubmitting }) => {
    const theme = useTheme();

    return (
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        disableRestoreFocus
        slotProps={{
          paper: {
            sx: {
              borderRadius: 4,
              p: 3,
              maxWidth: '600px',
            },
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            mb: 1,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WarningIcon size={24} color={theme.palette.orange[500]} weight="fill" />
            <Typography variant="textMD_semiBold" sx={{ color: 'text.primary' }}>
              <Trans i18nKey="assessments.summary.confirmModal.title" />
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small" sx={{ mt: -1, marginInlineEnd: -1 }}>
            <XIcon size={20} color="#222222" />
          </IconButton>
        </Box>

        {/* Warning message section */}
        <Paper
          elevation={0}
          sx={{
            mb: 2,
            mt: 2,
            backgroundColor: 'orange.100',
            border: '1px solid',
            borderColor: 'grey.300',
            borderRadius: 2,
            p: 2,
          }}
        >
          <Typography variant="textSM_regular" sx={{ color: 'text.primary' }}>
            <Trans i18nKey="assessments.summary.confirmModal.warningMessage" />
          </Typography>
        </Paper>

        {/* Evaluation info */}
        <Typography variant="textSM_regular" sx={{ color: 'text.tertiary', mb: 3 }}>
          <Trans i18nKey="assessments.summary.confirmModal.evaluationInfo" />
        </Typography>

        {/* Action buttons */}
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button
            variant="outlined"
            onClick={onClose}
            disabled={isSubmitting}
            sx={{
              px: 4.5,
              textTransform: 'none',
              backgroundColor: 'grey.200',
            }}
          >
            <Typography variant="textMD_medium">
              <Trans i18nKey="assessments.summary.confirmModal.cancel" />
            </Typography>
          </Button>
          <Button
            variant="contained"
            onClick={onConfirm}
            disabled={isSubmitting}
            sx={{
              borderRadius: 4,
              px: 3,
              py: 1.5,
              textTransform: 'none',
              backgroundColor: 'orange.500',
              '&:hover': {
                backgroundColor: '#FF5252',
              },
            }}
          >
            <PaperPlaneTiltIcon size={20} weight="fill" />
            <Typography variant="textMD_semiBold" sx={{ color: 'white' }}>
              <Trans
                i18nKey={
                  isSubmitting
                    ? 'assessments.summary.confirmModal.submitting'
                    : 'assessments.summary.confirmModal.confirm'
                }
              />
            </Typography>
          </Button>
        </Box>
      </Dialog>
    );
  },
);
